import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import { Modal, Form, Input, Button, Tag, List, Popover, message, Avatar, Checkbox, Divider, Spin, Tooltip } from 'antd';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useMyContext } from 'Context/MyContextProvider';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import apiClient from 'services/apiClient';

// Define badge color palette
const BADGE_COLORS = ["#a83271", "#f6b317", "#b18150", "#8b2a42", "#97b928", "#f4456c"];

const BadgeSelector = ({ list, allBadges, onBadgesUpdate, loading, setList,HandleBadgeClick }) => {
    const { isMobile, api, authToken, UserData,Permisson } = useMyContext();
    const [selectedBadges, setSelectedBadges] = useState([]);
    const [badgeOptions, setBadgeOptions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBadgeListOpen, setIsBadgeListOpen] = useState(false);
    const [currentBadge, setCurrentBadge] = useState(null);
    const [form] = Form.useForm();
    const [editingBadge, setEditingBadge] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    // Add ref for the scroll container
    const scrollContainerRef = useRef(null);

    // Get the next color in rotation
    const getNextColor = useCallback(() => {
        // If no badges exist yet, use the first color
        if (!badgeOptions.length) return BADGE_COLORS[0];

        // Count occurrences of each color
        const colorCounts = {};
        badgeOptions.forEach(badge => {
            if (badge.color) {
                colorCounts[badge.color] = (colorCounts[badge.color] || 0) + 1;
            }
        });

        // Find all colors with the minimum count
        let minCount = Infinity;
        let colorsWithMinCount = [];

        BADGE_COLORS.forEach(color => {
            const count = colorCounts[color] || 0;
            if (count < minCount) {
                minCount = count;
                colorsWithMinCount = [color];
            } else if (count === minCount) {
                colorsWithMinCount.push(color);
            }
        });

        // If multiple colors have the same minimum count,
        // select one randomly to ensure even distribution
        return colorsWithMinCount[Math.floor(Math.random() * colorsWithMinCount.length)];
    }, [badgeOptions]);

    // Define fixed container height
    const containerHeight = isMobile ? 300 : 550; // Adjust height based on mobile or desktop

    // Define these after searchTerm
    const [filteredUsers, setFilteredUsers] = useState([]);
    const initialItemsPerPage = 15;
    const [renderedRows, setRenderedRows] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);

    // Add loading state
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Add a new state to track which item is currently being moved
    const [movingItem, setMovingItem] = useState(null);

    // Filter users whenever searchTerm or list changes
    useEffect(() => {
        if (!list) {
            setFilteredUsers([]);
            return;
        }

        // Get users matching search term
        let filtered = list.filter(user =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Always sort to show selected users first
        filtered = [...filtered].sort((a, b) => {
            const aIsSelected = selectedUsers.some(user => user?.number === a.number);
            const bIsSelected = selectedUsers.some(user => user?.number === b.number);

            if (aIsSelected && !bIsSelected) return -1;
            if (!aIsSelected && bIsSelected) return 1;
            return 0;
        });

        setFilteredUsers(filtered);

        // Force re-render of the items with optimized animation
        if (movingItem) {
            setTimeout(() => {
                const initialItems = filtered.slice(0, initialItemsPerPage);
                setRenderedRows(initialItems);
                setHasMore(filtered.length > initialItemsPerPage);
            }, 300); // Add delay for animation
        }

    }, [list, searchTerm, selectedUsers, editingBadge, movingItem]);

    // Load badges from localStorage on component mount
    useEffect(() => {
        //console.log(allBadges, 'list')
        if (allBadges && allBadges.length > 0) {
            // First get the existing colors to avoid duplicates if possible
            const existingColors = allBadges
                .filter(badge => badge.color)
                .map(badge => badge.color);

            // Create a distribution map to track used colors
            const colorDistribution = {};
            BADGE_COLORS.forEach(color => {
                colorDistribution[color] = 0;
            });

            existingColors.forEach(color => {
                if (BADGE_COLORS.includes(color)) {
                    colorDistribution[color]++;
                }
            });

            const newBadgeOptions = allBadges.map(badge => {
                // If badge already has a color, use it
                if (badge.color && BADGE_COLORS.includes(badge.color)) {
                    return {
                        id: badge.id,
                        name: badge.title,
                        assignedUsers: badge.report_ids?.map(number => {
                            const foundUser = list?.find(user => parseInt(user.number) === parseInt(number));
                            return foundUser || { name: 'Unknown', number };
                        }) || [],
                        color: badge.color
                    };
                }

                // Find least used color
                let leastUsedColor = BADGE_COLORS[0];
                let minCount = Infinity;

                for (const [color, count] of Object.entries(colorDistribution)) {
                    if (count < minCount) {
                        minCount = count;
                        leastUsedColor = color;
                    }
                }

                // Increment the count for this color
                colorDistribution[leastUsedColor]++;

                return {
                    id: badge.id,
                    name: badge.title,
                    assignedUsers: badge.report_ids?.map(number => {
                        const foundUser = list?.find(user => parseInt(user.number) === parseInt(number));
                        return foundUser || { name: 'Unknown', number };
                    }) || [],
                    color: leastUsedColor
                };
            });

            setSelectedBadges(newBadgeOptions);
        }
    }, [allBadges, list]);



    const handleUserSelect = (user) => {
        // Set the moving item before updating selection
        if (!selectedUsers.some(item => item?.number === user?.number)) {
            // If selecting (not deselecting), mark as moving
            setMovingItem(user.number);

            // Clear the moving item after animation completes
            setTimeout(() => {
                setMovingItem(null);
            }, 500); // Match this with animation duration
        }

        setSelectedUsers(prev => {
            const isSelected = prev.some(item => item?.number === user?.number);
            if (isSelected) {
                return prev.filter(item => item?.number !== user?.number);
            } else {
                return [...prev, user];
            }
        });
    };

    const handleSearch = (value) => {
        setIsFiltering(true);
        setSearchTerm(value);
        setTimeout(() => {
            setIsFiltering(false);
        }, 300);
    };

    // Reset rendered rows when filtered users change
    useEffect(() => {
        // Initialize with first batch of users
        if (filteredUsers?.length > 0) {
            const initialItems = filteredUsers.slice(0, initialItemsPerPage);
            setRenderedRows(initialItems);
            setHasMore(filteredUsers.length > initialItemsPerPage);
        } else {
            setRenderedRows([]);
            setHasMore(false);
        }
    }, [filteredUsers, initialItemsPerPage]);

    // Function to load more items when scrolling
    const loadMore = useCallback(() => {
        if (isFiltering || !hasMore) return;

        // Show loader while loading more data
        setIsLoadingMore(true);

        // Add delay to simulate network loading (optional)
        setTimeout(() => {
            const currentSize = renderedRows.length;
            const nextItems = filteredUsers.slice(currentSize, currentSize + initialItemsPerPage);

            if (nextItems.length > 0) {
                setRenderedRows(prev => [...prev, ...nextItems]);
                setHasMore(currentSize + nextItems.length < filteredUsers.length);
            } else {
                setHasMore(false);
            }
            setIsLoadingMore(false);
        }, 500);
    }, [filteredUsers, renderedRows.length, hasMore, isFiltering]);

    // Render user item with animation
    const renderUserItem = (item, index) => {
        const isSelected = selectedUsers.some(user => user?.number === item?.number);
        const isMoving = movingItem === item.number;

        // Apply animation only to initial items, not to items loaded when scrolling
        const shouldAnimate = index < initialItemsPerPage;

        return (
            <motion.div
                key={item.number}
                initial={shouldAnimate ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
                animate={{
                    opacity: 1,
                    y: 0,
                    // Apply floating animation when selected
                    ...(isMoving && {
                        y: -20,
                        zIndex: 10
                    })
                }}
                transition={
                    isMoving ? {
                        duration: 0.4,
                        type: "spring",
                        stiffness: 300,
                        damping: 20
                    } : (
                        shouldAnimate ? {
                            duration: 0.3,
                            delay: index * 0.03,
                            ease: "easeOut"
                        } : {
                            duration: 0.2
                        }
                    )
                }
                layout // This handles smooth repositioning
            >
                <List.Item
                    style={{
                        cursor: 'pointer',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        backgroundColor: isSelected ? '#f0f0f0' : 'white',
                        border: '1px solid #e8e8e8',
                        transition: 'all 0.3s ease',
                        ...(isMoving && {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '1px solid #40a9ff'
                        })
                    }}
                    onClick={() => handleUserSelect(item)}
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleUserSelect(item);
                        }}
                        style={{ marginRight: 8 }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <List.Item.Meta
                        avatar={
                            <Avatar style={{ backgroundColor: '#f56a00' }}>
                                {item.name.charAt(0).toUpperCase()}
                            </Avatar>
                        }
                        title={item.name}
                        description={item.number}
                    />
                </List.Item>
            </motion.div>
        );
    };
    
    const openCreateBadgeModal = () => {
        setEditingBadge(null);
        form.resetFields();
        setIsModalOpen(true);
        setIsBadgeListOpen(false);
    };

    const openEditBadgeModal = (badge) => {
        setEditingBadge(badge);
        form.setFieldsValue({
            name: badge.name
        });
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setCurrentBadge(null);
        // Reset user selection data
        setSelectedUsers([]);
        setSearchTerm('');
    };

    // Set selected users when editing a badge
    useEffect(() => {
        if (editingBadge && editingBadge.assignedUsers) {
            setSelectedUsers(editingBadge.assignedUsers || []);
        } else {
            setSelectedUsers([]);
        }
    }, [editingBadge]);

    const handleCreateBadge = async (values) => {
        const newBadge = {
            name: values.name,
            assignedUsers: selectedUsers,
            color: editingBadge ? editingBadge.color : getNextColor()
        };
        await saveBadgeToAPI(newBadge);
    };

    const UpdateBadge = async (saveResult, newBadge) => {
        try {
            if (saveResult !== false) {
                if (editingBadge) {
                    // Update existing badge
                    setBadgeOptions(badgeOptions.map(badge =>
                        badge.id === editingBadge.id ? newBadge : badge
                    ));
                    // Also update if it's in selectedBadges
                    setSelectedBadges(selectedBadges.map(badge =>
                        badge.id === editingBadge.id ? newBadge : badge
                    ));
                } else {
                    // Create new badge and automatically select it if we haven't reached max
                    setBadgeOptions([...badgeOptions, newBadge]);

                    // Automatically select the new badge if we haven't reached max
                    if (selectedBadges.length < 5) {
                        setSelectedBadges([...selectedBadges, newBadge]);
                    }
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating badge state:', error);
            message.error('Failed to update badge: ' + (error.message || 'Unknown error'));
            return false;
        } finally {
            // Close modal regardless of success or failure
            setIsModalOpen(false);
        }
    }

    const deleteBadge = async (badgeId) => {
        try {
            // Show confirmation dialog before deletion
            Modal.confirm({
                title: 'Delete Badge',
                content: 'Are you sure you want to delete this badge?',
                okText: 'Yes, Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                async onOk() {
                    // Call the API to delete the badge
                    const response = await apiClient.delete(`${api}badges/${badgeId}`, {
                        headers: {
                            'Authorization': `Bearer ${authToken}`
                        },
                        params: {
                            user_id: UserData?.id
                        }
                    });

                    if (response.data.status) {
                        // Remove badge from local state
                        setBadgeOptions(badgeOptions.filter(badge => badge.id !== badgeId));
                        setSelectedBadges(selectedBadges.filter(badge => badge.id !== badgeId));
                        message.success('Badge deleted successfully');
                        onBadgesUpdate();
                    } else {
                        message.error(response.data.message || 'Failed to delete badge');
                    }
                }
            });
        } catch (error) {
            console.error('Error deleting badge:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete badge';
            message.error('Failed to delete badge: ' + errorMessage);
        }
    };
    const getUniqueColor = (existingColors) => {
        // If no colors exist yet, use the first color
        if (!existingColors || existingColors.length === 0) {
            return BADGE_COLORS[0];
        }

        // Find least used colors
        const colorCounts = {};
        BADGE_COLORS.forEach(color => {
            colorCounts[color] = 0;
        });

        existingColors.forEach(color => {
            if (color && BADGE_COLORS.includes(color)) {
                colorCounts[color] = (colorCounts[color] || 0) + 1;
            }
        });

        // Find the most recently used colors (last 2)
        const recentColors = existingColors.slice(-2);

        // Find colors with minimum count that aren't in recent colors
        let minCount = Math.min(...Object.values(colorCounts));
        let availableColors = BADGE_COLORS.filter(color =>
            colorCounts[color] === minCount && !recentColors.includes(color)
        );

        // If no colors meet both criteria, just use least used colors
        if (availableColors.length === 0) {
            availableColors = BADGE_COLORS.filter(color => colorCounts[color] === minCount);
        }

        // Still no colors? Just avoid the most recent color
        if (availableColors.length === 0) {
            availableColors = BADGE_COLORS.filter(color =>
                color !== existingColors[existingColors.length - 1]
            );
        }

        // Select randomly from available colors
        return availableColors[Math.floor(Math.random() * availableColors.length)];
    };
    const saveBadgeToAPI = async (badge) => {
        try {
            const badgeExists = badgeOptions.some(
                existingBadge =>
                    existingBadge.name.toLowerCase() === badge.name.toLowerCase() &&
                    existingBadge.id !== (editingBadge?.id || -1) // Ignore current badge when editing
            );

            if (badgeExists) {
                message.error('A badge with this name already exists');
                return false;
            }
            // Prepare data for API
            const payload = {
                title: badge.name,
                user_id: UserData?.id,
                badge_id: editingBadge?.id || null,
                report_ids: badge.assignedUsers?.map(user => user.number) ?? []
            };

            const response = await apiClient.post(`${api}badges`, payload, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            if (response.data.status) {
                const badgeColor = editingBadge ?
                    editingBadge.color :
                    getUniqueColor(badgeOptions.map(b => b.color).filter(Boolean));
                const newBadge = {
                    id: response.data.badges.id,
                    name: response.data.badges.title,
                    assignedUsers: response?.data?.badges?.report_ids?.map(number => {
                        // Find matching user from list if available
                        const foundUser = list?.find(user => user.number === number);
                        return foundUser || { name: 'Unknown', number };
                    }) ?? [],
                    color: badgeColor
                }
                onBadgesUpdate();
                UpdateBadge(response.data.status, newBadge)
                UpdateChat(badge, newBadge);
                message.success('Badge saved successfully');
            }
            //return response.data;
        } catch (error) {
            console.error('Error saving badge to API:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save badge';
            message.error('Failed to save badge: ' + errorMessage);
            return false;
        }
    };

    const UpdateChat = (badge, newBadge) => {
        if (!list || list.length === 0) return;

        const updatedList = [...list];

        // === 1. Add or update badge for assigned users ===
        badge.assignedUsers?.forEach(assignedUser => {
            const userIndex = updatedList.findIndex(user => user.number === assignedUser.number);
            if (userIndex === -1) return;

            const user = { ...updatedList[userIndex] }; // Clone to avoid direct mutation

            user.badges = user.badges ? [...user.badges] : [];

            // Remove old version if editing
            if (editingBadge?.id) {
                user.badges = user.badges.filter(b => b.id !== editingBadge.id);
            }

            // Add new badge if not already present
            if (!user.badges.some(b => b.id === newBadge.id)) {
                user.badges.push({
                    id: newBadge.id,
                    title: newBadge.name,
                    color: newBadge.color
                });
            }

            updatedList[userIndex] = user;
        });

        // === 2. Remove badge from users no longer assigned ===
        if (editingBadge?.assignedUsers?.length && editingBadge.id) {
            const removedUsers = editingBadge.assignedUsers.filter(
                oldUser => !badge.assignedUsers?.some(newUser => newUser.number === oldUser.number)
            );

            removedUsers.forEach(removedUser => {
                const userIndex = updatedList.findIndex(user => user.number === removedUser.number);
                if (userIndex === -1) return;

                const user = { ...updatedList[userIndex] };

                if (user.badges?.length) {
                    user.badges = user.badges.filter(b => b.id !== editingBadge.id);
                    updatedList[userIndex] = user;
                }
            });
        }

        // === 3. Apply updates ===
        setList(updatedList);
    };


    // Function to scroll the badge container when arrow is clicked
    const handleArrowClick = (direction) => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = direction === 'right' ? 150 : -150;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const HandleBadgeSelect = (badge) => {
        HandleBadgeClick(badge);
    }

    return (
        <>
            <div className="d-flex align-items-center px-3 badge-selector-container" style={{ position: 'relative', overflow: 'hidden' }}>
                <div
                    className="d-flex align-items-center badge-scroll-container"
                    ref={scrollContainerRef}
                    style={{
                        maxHeight: '50px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '18rem',
                        // padding: '8px 0',
                        cursor: 'grab',
                        WebkitOverflowScrolling: 'touch',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}
                    onMouseDown={(e) => {
                        // Store initial mouse position and scroll position
                        const slider = e.currentTarget;
                        const startX = e.pageX - slider.offsetLeft;
                        const scrollLeft = slider.scrollLeft;

                        const onMouseMove = (e) => {
                            e.preventDefault();
                            // Calculate how far the mouse has moved
                            const x = e.pageX - slider.offsetLeft;
                            const distance = x - startX;
                            // Scroll the container
                            slider.scrollLeft = scrollLeft - distance;
                            // Change cursor while dragging
                            slider.style.cursor = 'grabbing';
                        };

                        const onMouseUp = () => {
                            document.removeEventListener('mousemove', onMouseMove);
                            document.removeEventListener('mouseup', onMouseUp);
                            // Reset cursor
                            slider.style.cursor = 'grab';
                        };

                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                    }}
                >
                    {/* Add global styles for hiding scrollbars across browsers */}
                    <style>{`
                        /* Global styles to hide scrollbars */
                        .badge-scroll-container::-webkit-scrollbar {
                            width: 0 !important;
                            height: 0 !important;
                            display: none !important;
                        }
                        
                        .badge-scroll-container {
                            -ms-overflow-style: none !important;
                            scrollbar-width: none !important;
                        }
                    `}</style>

                    {selectedBadges?.map(badge => (
                        <motion.div
                            key={badge.id}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{
                                opacity: 0,
                                scale: 0.8,
                                x: -30,
                                transition: { duration: 0.2 }
                            }}
                            layout
                        >
                            <Tag
                                key={badge.id}
                                className="badge-item m-1 rounded d-flex align-items-center"
                                style={{
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    backgroundColor: badge.color || BADGE_COLORS[0],
                                    color: '#fff',
                                    border: 'none'
                                }}
                                onClick={() => HandleBadgeSelect(badge)}
                            >
                                <span>{badge.name}</span>
                                {badge.assignedUsers && badge.assignedUsers.length > 0 && (
                                    <small style={{ marginLeft: 4, marginRight: 4 }}>
                                        ({badge.assignedUsers.length})
                                    </small>
                                )}
                                <Popover
                                    content={
                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
                                            {[
                                                {
                                                    label: 'Edit',
                                                    icon: <EditOutlined />,
                                                    onClick: () => openEditBadgeModal(badge),
                                                    danger: false
                                                },
                                                {
                                                    label: 'Delete',
                                                    icon: <DeleteOutlined />,
                                                    onClick: () => deleteBadge(badge.id),
                                                    danger: true,
                                                    disabled: badge.assignedUsers && badge.assignedUsers.length > 0,
                                                    tooltip: badge.assignedUsers && badge.assignedUsers.length > 0 ?
                                                        `Cannot delete badge with ${badge.assignedUsers.length} assigned contact${badge.assignedUsers.length > 1 ? 's' : ''}` : ''
                                                }
                                            ].map((action, index) => (
                                                <Tooltip title={action?.tooltip} placement="right">
                                                    <Button
                                                        type="text"
                                                        icon={action.icon}
                                                        onClick={action.onClick}
                                                        danger={action.danger}
                                                        disabled={action.disabled}
                                                        style={{
                                                            textAlign: 'left',
                                                            opacity: action.disabled ? 0.5 : 1,
                                                            cursor: action.disabled ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        {action.label}
                                                    </Button>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    }
                                    trigger="click"
                                    overlayInnerStyle={{ padding: 0 }}
                                    overlayClassName="zero-padding-popover"
                                    placement="rightTop"
                                >
                                    <MoreOutlined style={{ marginLeft: '4px', fontSize: '14px' }} />
                                </Popover>
                            </Tag>
                        </motion.div>
                    ))}
                    {Permisson.includes('Create Chat Badge') && selectedBadges?.length < 5 && (
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Tag
                                className="add-badge-button m-1 rounded"
                                style={{ cursor: 'pointer' }}
                                onClick={openCreateBadgeModal}
                            >
                                <PlusOutlined />
                                {selectedBadges.length === 0 && <span style={{ marginLeft: 4 }}>Add badge</span>}
                            </Tag>
                        </motion.div>
                    )}
                </div>
                {selectedBadges?.length > 0 && (
                    <ChevronRight
                        onClick={() => handleArrowClick('right')}
                        style={{ cursor: 'pointer' }} />
                )}
            </div>

            {/* Create/Edit Badge Modal */}
            <Modal
                title={editingBadge ? "Edit Badge" : "Create New Badge"}
                open={isModalOpen}
                onCancel={handleModalClose}
                footer={null}
                className="create-badge-modal"
                width={700}
                style={{ top: 20 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateBadge}
                    destroyOnClose={true}
                >
                    <Form.Item
                        name="name"
                        label="Badge Name"
                        rules={[

                            {
                                required: true,
                                message: 'Please enter a badge name',
                                validateTrigger: 'onBlur'
                            }
                        ]}
                        validateStatus={form.getFieldError('name')?.length ? 'error' : ''}
                        help={form.getFieldError('name')?.[0]}
                    >
                        <Input placeholder="Enter badge name" maxLength={15} />
                    </Form.Item>

                    <Divider>Assign Contacts</Divider>

                    <Input
                        placeholder="Search contacts"
                        prefix={<SearchOutlined />}
                        onChange={(e) => handleSearch(e.target.value)}
                        value={searchTerm}
                        style={{ marginBottom: 16 }}
                    />
                    <div
                        id="scrollableDiv"
                        style={{
                            height: containerHeight,
                            overflow: 'auto',
                            // border: '1px solid rgba(140, 140, 140, 0.35)',
                            borderRadius: '4px',
                            padding: '0 12px',
                            // Hide default scrollbar to avoid double scrollbars
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            }
                        }}
                    >
                        <InfiniteScroll
                            dataLength={renderedRows.length}
                            next={loadMore}
                            hasMore={hasMore}
                            loader={null} // Remove default loader
                            scrollableTarget="scrollableDiv"
                            endMessage={
                                renderedRows.length > 0 ? (
                                    <p style={{ textAlign: 'center', padding: '10px', color: '#888' }}>
                                        All contacts loaded
                                    </p>
                                ) : null
                            }
                            scrollThreshold={0.8}
                            style={{ overflow: 'visible' }} // Prevent inner scrollbar
                        >
                            <List
                                dataSource={renderedRows}
                                renderItem={(item, index) => renderUserItem(item, index)}
                                loading={false} // Remove List loading state
                            />

                            {/* Single unified loader at the bottom */}
                            {isLoadingMore && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '10px',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 1
                                }}>
                                    <Spin size="small" />
                                    <p style={{ marginTop: 4, fontSize: '12px', color: '#888' }}>
                                        Loading more contacts...
                                    </p>
                                </div>
                            )}
                        </InfiniteScroll>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                        <Button
                            onClick={handleModalClose}
                            style={{ marginRight: 8 }}
                        >
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingBadge ? "Update" : "Create"}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default BadgeSelector;